import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DsIconComponent } from '../icons/ds-icon.component';

export type DsStepState = 'incoming' | 'current' | 'complete';
export type DsStepperOrientation = 'horizontal' | 'vertical';

export interface DsStep {
  title: string;
  description?: string;
  state: DsStepState;
  isSelected?: boolean;
  clickable?: boolean;
}

@Component({
  selector: 'ds-stepper',
  standalone: true,
  imports: [CommonModule, DsIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- HORIZONTAL -->
    <div *ngIf="orientation === 'horizontal'" class="ds-stepper ds-stepper--h">
      <div
        *ngFor="let step of steps; let i = index; let first = first; let last = last"
        class="ds-step ds-step--h"
        [class.ds-step--incoming]="step.state === 'incoming'"
        [class.ds-step--current]="step.state === 'current'"
        [class.ds-step--complete]="step.state === 'complete'"
        [class.ds-step--selected]="step.isSelected"
        [class.ds-step--clickable]="step.clickable"
        [attr.tabindex]="step.clickable ? 0 : null"
        (click)="step.clickable && stepClick.emit(i)"
        (keydown.enter)="step.clickable && stepClick.emit(i)"
        (keydown.space)="step.clickable && stepClick.emit(i)"
      >
        <div class="ds-step__connector-row">
          <div *ngIf="!first"
               class="ds-step__line ds-step__line--h"
               [style.background-color]="lineColor(i - 1)"
               [style.opacity]="step.isSelected ? 0.3 : 1"></div>
          <div *ngIf="first" class="ds-step__spacer"></div>

          <div class="ds-step__dot-area">
            <div *ngIf="step.state === 'incoming'" class="ds-step__dot ds-step__dot--incoming">
              <span class="ds-step__dot-num ds-text--visual-padding-none">{{ i + 1 }}</span>
            </div>
            <div *ngIf="step.state === 'current'" class="ds-step__dot ds-step__dot--current">
              <span class="ds-step__dot-num ds-text--visual-padding-none">{{ i + 1 }}</span>
            </div>
            <div *ngIf="step.state === 'complete'" class="ds-step__dot ds-step__dot--complete">
              <ds-icon class="ds-step__check" name="check" size="var(--ds-stepper-dot-size-s)"></ds-icon>
            </div>
          </div>

          <div *ngIf="!last"
               class="ds-step__line ds-step__line--h"
               [style.background-color]="lineColor(i)"
               [style.opacity]="step.isSelected ? 0.3 : 1"></div>
          <div *ngIf="last" class="ds-step__spacer"></div>
        </div>

        <div class="ds-step__text-col--h">
          <span class="ds-step__title">{{ step.title }}</span>
          <span *ngIf="step.description" class="ds-step__description">{{ step.description }}</span>
        </div>
      </div>
    </div>

    <!-- VERTICAL -->
    <div *ngIf="orientation === 'vertical'" class="ds-stepper ds-stepper--v">
      <div
        *ngFor="let step of steps; let i = index; let first = first; let last = last"
        class="ds-step ds-step--v"
        [class.ds-step--incoming]="step.state === 'incoming'"
        [class.ds-step--current]="step.state === 'current'"
        [class.ds-step--complete]="step.state === 'complete'"
        [class.ds-step--selected]="step.isSelected"
        [class.ds-step--clickable]="step.clickable"
        [attr.tabindex]="step.clickable ? 0 : null"
        (click)="step.clickable && stepClick.emit(i)"
        (keydown.enter)="step.clickable && stepClick.emit(i)"
        (keydown.space)="step.clickable && stepClick.emit(i)"
      >
        <div class="ds-step__dot-col">
          <div class="ds-step__line ds-step__line--v ds-step__line--entry"
               [style.background-color]="!first ? lineColor(i - 1) : 'transparent'"
               [style.opacity]="step.isSelected ? 0.3 : 1"></div>

          <div class="ds-step__dot-area">
            <div *ngIf="step.state === 'incoming'" class="ds-step__dot ds-step__dot--incoming">
              <span class="ds-step__dot-num ds-text--visual-padding-none">{{ i + 1 }}</span>
            </div>
            <div *ngIf="step.state === 'current'" class="ds-step__dot ds-step__dot--current">
              <span class="ds-step__dot-num ds-text--visual-padding-none">{{ i + 1 }}</span>
            </div>
            <div *ngIf="step.state === 'complete'" class="ds-step__dot ds-step__dot--complete">
              <ds-icon class="ds-step__check" name="check" size="var(--ds-stepper-dot-size-s)"></ds-icon>
            </div>
          </div>

          <div *ngIf="!last"
               class="ds-step__line ds-step__line--v ds-step__line--exit"
               [style.background-color]="lineColor(i)"
               [style.opacity]="step.isSelected ? 0.3 : 1"></div>
          <div *ngIf="last" class="ds-step__spacer"></div>
        </div>

        <div class="ds-step__text-col"
             [class.ds-step__text-col--selected]="step.isSelected">
          <span class="ds-step__title">{{ step.title }}</span>
          <span *ngIf="step.description" class="ds-step__description">{{ step.description }}</span>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }

    /* ── Container ──────────────────────────────────────────────────── */
    .ds-stepper--h {
      display: flex;
      align-items: flex-start;
    }

    .ds-stepper--v {
      display: flex;
      flex-direction: column;
    }

    /* ── Horizontal step ────────────────────────────────────────────── */
    .ds-step--h {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--ds-stepper-horizontal-content-gap);
      padding: var(--ds-stepper-horizontal-padding-vertical) 0;
      border-radius: var(--ds-stepper-border-radius);
      cursor: default;
      transition: background 300ms ease, border-radius 300ms ease, box-shadow 300ms ease;
    }
    .ds-step__text-col--h {
      width: 100%;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--ds-stepper-text-container-gap);
      padding-left: var(--ds-stepper-text-container-padding-horizontal);
      padding-right: var(--ds-stepper-text-container-padding-horizontal);
    }
    .ds-step__text-col--h .ds-step__title,
    .ds-step__text-col--h .ds-step__description {
      text-align: center;
    }
    .ds-step--h.ds-step--clickable {
      cursor: pointer;
    }
    .ds-step--h.ds-step--clickable:hover:not(.ds-step--selected) {
      background:
        linear-gradient(var(--ds-stepper-hover-overlay), var(--ds-stepper-hover-overlay)),
        transparent;
      background-blend-mode: multiply;
      border-radius: calc(var(--ds-stepper-border-radius) + var(--ds-stepper-hover-border-radius-delta));
    }
    .ds-step--h.ds-step--selected {
      background: var(--ds-stepper-selected-background);
    }
    .ds-step--h:focus-visible {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-step__connector-row {
      display: flex;
      align-items: center;
      gap: var(--ds-stepper-gap);
      width: 100%;
    }

    .ds-step__line--h {
      flex: 1;
      height: var(--ds-stepper-line-thickness);
    }

    .ds-step__spacer {
      flex: 1;
    }

    /* ── Vertical step ──────────────────────────────────────────────── */
    .ds-step--v {
      display: flex;
      flex-direction: row;
      padding: 0 var(--ds-stepper-step-padding-horizontal);
      border-radius: var(--ds-stepper-border-radius);
      cursor: default;
      transition: background 300ms ease, border-radius 300ms ease, box-shadow 300ms ease;
      min-height: var(--ds-stepper-min-height);
    }
    .ds-step--v.ds-step--clickable {
      cursor: pointer;
    }
    .ds-step--v.ds-step--clickable:hover:not(.ds-step--selected) {
      background:
        linear-gradient(var(--ds-stepper-hover-overlay), var(--ds-stepper-hover-overlay)),
        transparent;
      background-blend-mode: multiply;
      border-radius: calc(var(--ds-stepper-border-radius) + var(--ds-stepper-hover-border-radius-delta));
    }
    .ds-step--v.ds-step--selected {
      background: var(--ds-stepper-selected-background);
    }
    .ds-step--v:focus-visible {
      outline: var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }

    .ds-step__dot-col {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--ds-stepper-gap);
      width: var(--ds-stepper-dot-area-size);
      flex-shrink: 0;
    }

    .ds-step__line--v {
      width: var(--ds-stepper-line-thickness);
    }
    .ds-step__line--entry {
      height: var(--ds-stepper-line-entry-height);
      flex-shrink: 0;
    }
    .ds-step__line--exit {
      flex: 1;
      min-height: var(--ds-stepper-line-entry-height);
    }

    .ds-step__text-col {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      gap: var(--ds-stepper-text-container-gap);
      padding-top: var(--ds-stepper-text-container-padding-vertical);
      padding-bottom: var(--ds-stepper-text-container-padding-bottom);
      padding-left: var(--ds-stepper-text-container-padding-horizontal);
      padding-right: var(--ds-stepper-text-container-padding-horizontal);
    }
    .ds-step__text-col--selected {
      padding-bottom: var(--ds-stepper-text-container-padding-vertical);
    }

    /* ── Dot area ───────────────────────────────────────────────────── */
    .ds-step__dot-area {
      width: var(--ds-stepper-dot-area-size);
      height: var(--ds-stepper-dot-area-size);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .ds-step__dot {
      width: var(--ds-stepper-dot-size-l);
      height: var(--ds-stepper-dot-size-l);
      border-radius: 50%;
      display: grid;
      place-items: center;
      flex-shrink: 0;
      position: relative;
    }

    .ds-step__dot--incoming {
      background: var(--ds-stepper-incoming-dot-color);
    }

    .ds-step__dot--current {
      background: var(--ds-stepper-current-dot-color);
    }

    .ds-step__dot--complete {
      background: var(--ds-stepper-complete-dot-color);
    }

    .ds-step__dot-num {
      position: absolute;
      top: 50%;
      left: 50%;
      font-family: 'Sura Sans', sans-serif;
      font-size: var(--ds-stepper-dot-number-font-size);
      font-weight: var(--ds-stepper-dot-number-font-weight);
      line-height: 1;
      text-align: center;
      transform: translate(-50%, -50%);
    }

    .ds-text--visual-padding-none {
      text-box-trim: trim-both;
    }

    .ds-step__dot--incoming .ds-step__dot-num { color: var(--ds-stepper-incoming-dot-content-color); }
    .ds-step__dot--current  .ds-step__dot-num { color: var(--ds-stepper-current-dot-content-color); }

    .ds-step__check {
      width: var(--ds-stepper-dot-size-s);
      height: var(--ds-stepper-dot-size-s);
      color: var(--ds-stepper-complete-dot-content-color);
    }

    /* ── Text ───────────────────────────────────────────────────────── */
    .ds-step__title {
      font-family: 'Sura Sans', sans-serif;
      font-size: var(--ds-stepper-title-font-size);
      line-height: var(--ds-stepper-title-line-height);
      font-weight: var(--ds-stepper-title-font-weight);
    }

    .ds-step__description {
      font-family: 'Sura Sans', sans-serif;
      font-size: var(--ds-stepper-description-font-size);
      line-height: var(--ds-stepper-description-line-height);
      font-weight: var(--ds-stepper-description-font-weight);
    }

    /* State-based text colors */
    .ds-step--incoming .ds-step__title       { color: var(--ds-stepper-incoming-title-color); }
    .ds-step--current  .ds-step__title       { color: var(--ds-stepper-current-title-color); }
    .ds-step--complete .ds-step__title       { color: var(--ds-stepper-complete-title-color); }
    .ds-step--incoming .ds-step__description { color: var(--ds-stepper-incoming-description-color); }
    .ds-step--current  .ds-step__description { color: var(--ds-stepper-current-description-color); }
    .ds-step--complete .ds-step__description { color: var(--ds-stepper-complete-description-color); }
  `],
})
export class DsStepperComponent {
  @Input() steps: DsStep[] = [];
  @Input() orientation: DsStepperOrientation = 'horizontal';

  @Output() stepClick = new EventEmitter<number>();

  lineColor(stepIndex: number): string {
    const step = this.steps[stepIndex];
    return step?.state === 'complete'
      ? 'var(--ds-stepper-line-complete-color)'
      : 'var(--ds-stepper-line-default-color)';
  }
}
